import moment from 'moment';

/** Last sync happened less than an hour ago. */
export const SYNC_STATUS_FRESH = 'fresh';
/** Last sync happened between one and six hours ago. */
export const SYNC_STATUS_RECENT = 'recent';
/** Last sync happened more than six hours ago, or is unknown. */
export const SYNC_STATUS_STALE = 'stale';

const MINUTE_IN_MS = 60 * 1000;
const HOUR_IN_MS = 60 * MINUTE_IN_MS;
const DAY_IN_MS = 24 * HOUR_IN_MS;
const RELATIVE_TIME_MAX_DAYS = 7;

const ABSOLUTE_DATE_FORMAT = 'MMM D, YYYY';
const ABSOLUTE_DATE_TIME_FORMAT = 'MMM D, YYYY [at] h:mm A';

/** How often the relative label must be recomputed so it stays accurate without a page reload. */
export const RELATIVE_TIME_REFRESH_INTERVAL_MS = MINUTE_IN_MS;

/** Toggles the colored freshness dot; when off only the relative time label is rendered. */
export const SHOW_SYNC_STATUS_DOT = true;

/**
 * Normalizes the backend synchronization timestamp into a moment instance.
 * The API returns the value already expressed in the project's local time but suffixed
 * with `Z`, so the UTC markers are stripped before parsing to avoid a spurious shift.
 * @param {string|Date|import('moment').Moment|null|undefined} value - Raw last-sync timestamp.
 * @returns {import('moment').Moment|null} Parsed timestamp, or `null` when missing or invalid.
 */
export const parseSyncDate = (value) => {
    if (!value) {
        return null;
    }

    const normalizedValue =
        typeof value === 'string' ? value.replace('Z', '').replace('T', ' ') : value;
    const parsed = moment(normalizedValue);

    return parsed.isValid() ? parsed : null;
};

/**
 * Builds the human readable elapsed time since the last synchronization.
 * @param {import('moment').Moment|null} syncDate - Parsed last-sync timestamp.
 * @param {import('moment').Moment} [now] - Reference instant, defaults to the current time.
 * @returns {string} `just now`, `X minutes ago`, `X hours ago`, `X days ago`, or the absolute
 *   date (e.g. `Jul 8, 2026`) once a week or more has elapsed. Empty string when unknown.
 */
export const getRelativeSyncTime = (syncDate, now = moment()) => {
    if (!syncDate) {
        return '';
    }

    const elapsedMs = now.diff(syncDate);

    if (elapsedMs < MINUTE_IN_MS) {
        return 'just now';
    }

    if (elapsedMs < HOUR_IN_MS) {
        const minutes = Math.floor(elapsedMs / MINUTE_IN_MS);

        return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    }

    if (elapsedMs < DAY_IN_MS) {
        const hours = Math.floor(elapsedMs / HOUR_IN_MS);

        return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }

    const days = Math.floor(elapsedMs / DAY_IN_MS);

    if (days < RELATIVE_TIME_MAX_DAYS) {
        return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }

    return syncDate.format(ABSOLUTE_DATE_FORMAT);
};

/**
 * Classifies data freshness so the indicator can pick a dot color.
 * @param {import('moment').Moment|null} syncDate - Parsed last-sync timestamp.
 * @param {import('moment').Moment} [now] - Reference instant, defaults to the current time.
 * @returns {string} `fresh` (< 1h), `recent` (1-6h) or `stale` (> 6h or unknown timestamp).
 */
export const getSyncStatusLevel = (syncDate, now = moment()) => {
    if (!syncDate) {
        return SYNC_STATUS_STALE;
    }

    const elapsedMs = now.diff(syncDate);

    if (elapsedMs < HOUR_IN_MS) {
        return SYNC_STATUS_FRESH;
    }

    if (elapsedMs <= 6 * HOUR_IN_MS) {
        return SYNC_STATUS_RECENT;
    }

    return SYNC_STATUS_STALE;
};

/**
 * Formats the UTC offset of a timestamp as a short label.
 * @param {import('moment').Moment} syncDate - Parsed last-sync timestamp.
 * @returns {string} Offset label such as `UTC-5`, `UTC+5:30` or `UTC` when there is no offset.
 */
const formatUtcOffset = (syncDate) => {
    const offsetInMinutes = syncDate.utcOffset();

    if (offsetInMinutes === 0) {
        return 'UTC';
    }

    const sign = offsetInMinutes < 0 ? '-' : '+';
    const absoluteOffset = Math.abs(offsetInMinutes);
    const hours = Math.floor(absoluteOffset / 60);
    const minutes = absoluteOffset % 60;

    return `UTC${sign}${hours}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}`;
};

/**
 * Builds the hover tooltip holding the full absolute timestamp of the last synchronization.
 * @param {import('moment').Moment|null} syncDate - Parsed last-sync timestamp.
 * @returns {string} e.g. `Last synchronized: Jul 8, 2026 at 1:10 PM (UTC-5)`, or a fallback
 *   message when the timestamp is unknown.
 */
export const buildSyncTooltip = (syncDate) => {
    if (!syncDate) {
        return 'Last synchronization date is not available';
    }

    return `Last synchronized: ${syncDate.format(ABSOLUTE_DATE_TIME_FORMAT)} (${formatUtcOffset(
        syncDate
    )})`;
};

/**
 * Builds the visible label of the indicator.
 * @param {import('moment').Moment|null} syncDate - Parsed last-sync timestamp.
 * @param {import('moment').Moment} [now] - Reference instant, defaults to the current time.
 * @returns {string} e.g. `Data synchronized 2 hours ago`, or a fallback when unknown.
 */
export const buildSyncLabel = (syncDate, now = moment()) => {
    if (!syncDate) {
        return 'Data synchronization date unavailable';
    }

    return `Data synchronized ${getRelativeSyncTime(syncDate, now)}`;
};
